import apiClient from '../apiClient';

const mapOrder = (order) => {
  if (!order) return null;
  return {
    id: order.id,
    orderNumber: order.order_number,
    customerInfo: {
      fullName: order.shipping_address?.fullName || '',
      email: order.email,
      phone: order.phone || '',
      address: order.shipping_address?.address || '',
      city: order.shipping_address?.city || '',
      district: order.shipping_address?.district || '',
    },
    items: (order.items || []).map((item) => ({
      productId: item.product_id,
      name: item.name,
      price: item.unit_price,
      quantity: item.quantity,
      size: item.size || null,
      color: item.color || null,
      image: item.image || '',
    })),
    total: order.total_amount,
    status: order.status,
    paymentMethod: order.payment_method,
    createdAt: order.created_at,
    statusHistory: (order.status_history || []).map((entry) => ({
      status: entry.status,
      changedAt: entry.at || entry.changedAt,
      changedBy: entry.changedBy,
      note: entry.note,
    })),
  };
};

/**
 * Get all orders with pagination and filters
 * @param {Object} filters - Filter options
 * @param {string} filters.status - Order status filter
 * @param {Date} filters.startDate - Start date filter
 * @param {Date} filters.endDate - End date filter
 * @param {string} filters.search - Search by order number or email
 * @param {number} pageSize - Number of orders per page
 * @param {DocumentSnapshot} lastDoc - Last document for pagination
 * @returns {Promise<{orders: Array, lastDoc: DocumentSnapshot, hasMore: boolean}>}
 */
export const getOrdersPaginated = async (
  filters = {},
  pageSize = 20,
  page = 1
) => {
  try {
    const response = await apiClient.get('/admin/orders', {
      params: {
        page,
        page_size: pageSize,
        status: filters.status,
        search: filters.search,
      },
    });
    let orders = response.data.map(mapOrder);
    if (filters.startDate) {
      orders = orders.filter((order) => new Date(order.createdAt) >= filters.startDate);
    }
    if (filters.endDate) {
      orders = orders.filter((order) => new Date(order.createdAt) <= filters.endDate);
    }
    const hasMore = orders.length === pageSize;
    return { orders, nextPage: page + 1, hasMore };
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
};

/**
 * Get order by ID
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} Order object
 */
export const getOrderById = async (orderId) => {
  try {
    const response = await apiClient.get(`/admin/orders/${orderId}`);
    return mapOrder(response.data);
  } catch (error) {
    console.error('Error fetching order:', error);
    throw error;
  }
};

/**
 * Update order status with history tracking
 * @param {string} orderId - Order ID
 * @param {string} status - New status
 * @param {Object} statusHistoryEntry - Status history entry
 * @returns {Promise<void>}
 */
export const updateOrderStatus = async (orderId, status, statusHistoryEntry = null) => {
  try {
    const payload = { status, note: statusHistoryEntry?.note };
    const response = await apiClient.patch(`/admin/orders/${orderId}/status`, payload);
    return mapOrder(response.data);
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
};

/**
 * Send manual email to customer
 * @param {string} orderId - Order ID
 * @param {Object} payload - Email payload
 * @returns {Promise<Object>} Response
 */
export const sendOrderEmail = async (orderId, payload) => {
  try {
    const response = await apiClient.post(`/admin/orders/${orderId}/email`, payload);
    return response.data;
  } catch (error) {
    console.error('Error sending order email:', error);
    throw error;
  }
};

/**
 * Get order statistics
 * @returns {Promise<Object>} Statistics object
 */
export const getOrderStatistics = async () => {
  try {
    const response = await apiClient.get('/admin/orders/statistics');
    return response.data;
  } catch (error) {
    console.error('Error fetching order statistics:', error);
    throw error;
  }
};
